import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { User } from "@/shared/types";
import { configureAxiosAuth } from "@/lib/axiosClient";

interface AuthState {
  accessToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  _hasHydrated: boolean; // Track if store has been hydrated from localStorage
  // Actions
  setAuth: (accessToken: string, user: User) => void;
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  clearAuth: () => void;
  getAuthToken: () => string | null; // Backward compatibility alias
  hasAuthToken: () => boolean;
  getAuthHeaders: () => Record<string, string>;
  setHasHydrated: (state: boolean) => void;
}

// Safe localStorage access for SSR
const getStorage = () => {
  if (typeof window === 'undefined') {
    // Return a no-op storage for SSR
    return {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
    };
  }
  return localStorage;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,
      _hasHydrated: false,

      setAuth: (accessToken: string, user: User) => {
        // Remove password from user if present
        const { password: _, ...userWithoutPassword } = user as any;
        set({
          accessToken,
          user: userWithoutPassword as User,
          isAuthenticated: true,
        });
      },

      setUser: (user: User | null) => {
        if (user) {
          // Remove password from user if present
          const { password: _, ...userWithoutPassword } = user as any;
          set({
            user: userWithoutPassword as User,
            isAuthenticated: !!get().accessToken,
          });
        } else {
          set({
            user: null,
            isAuthenticated: false,
          });
        }
      },

      setAccessToken: (token: string | null) => {
        set({
          accessToken: token,
          isAuthenticated: !!token && !!get().user,
        });
      },

      clearAuth: () => {
        set({
          accessToken: null,
          user: null,
          isAuthenticated: false,
        });
      },

      getAuthToken: () => {
        // Backward compatibility: return accessToken
        return get().accessToken;
      },

      hasAuthToken: () => {
        return get().accessToken !== null;
      },

      getAuthHeaders: () => {
        const token = get().accessToken;
        const headers: Record<string, string> = {};
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
        return headers;
      },

      setHasHydrated: (state: boolean) => {
        set({
          _hasHydrated: state,
        });
      },
    }),
    {
      name: "auth-storage", // unique name for localStorage key
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state, error) => {
        // Mark as hydrated after rehydration completes (or fails)
        if (state) {
          state.setHasHydrated(true);
        } else if (error) {
          // If rehydration fails, still mark as hydrated to prevent infinite loading
          // The store will start with default values
          useAuthStore.getState().setHasHydrated(true);
        }
      },
    }
  )
);

// Configure axios to use this store's token getter/setter
configureAxiosAuth(
  () => useAuthStore.getState().accessToken,
  (token) => useAuthStore.getState().setAccessToken(token)
);

// Helper functions for backward compatibility
export const getAuthToken = (): string | null => {
  return useAuthStore.getState().getAuthToken();
};

export const setAuthToken = (token: string): void => {
  useAuthStore.getState().setAccessToken(token);
};

export const removeAuthToken = (): void => {
  useAuthStore.getState().clearAuth();
};

export const hasAuthToken = (): boolean => {
  return useAuthStore.getState().hasAuthToken();
};

export const getAuthHeaders = (): Record<string, string> => {
  return useAuthStore.getState().getAuthHeaders();
};

